import { SKILL_IDS, STRATEGY_IDS } from "../constants.ts";
import { factId } from "../ids.ts";
import {
  LESSON_PHASES,
  type FactId,
  type LessonDefinition,
  type LessonStepDefinition,
  type StrategyDefinition,
} from "../model.ts";

export const STRATEGY_CATALOG_VERSION = "r20-strategies-v1";

interface LessonCopy {
  readonly explanation: readonly string[];
  readonly worked: readonly string[];
  readonly guided: readonly string[];
  readonly partial: readonly string[];
  readonly independent: readonly string[];
}

function lesson(
  id: string,
  title: string,
  generatorPrefix: string,
  exampleFactIds: readonly FactId[],
  copy: LessonCopy,
): LessonDefinition {
  const steps: readonly LessonStepDefinition[] = [
    {
      phase: LESSON_PHASES[0],
      title,
      copy: copy.explanation,
      generatorId: `${generatorPrefix}.explanation`,
      responseMode: null,
      supportLevel: null,
      retrievalEvidence: false,
      strategyEvidence: false,
    },
    {
      phase: LESSON_PHASES[1],
      title: "Kijk mee",
      copy: copy.worked,
      generatorId: `${generatorPrefix}.worked`,
      responseMode: null,
      supportLevel: "FULL",
      retrievalEvidence: false,
      strategyEvidence: false,
    },
    {
      phase: LESSON_PHASES[2],
      title: "Doe mee",
      copy: copy.guided,
      generatorId: `${generatorPrefix}.guided`,
      responseMode: "GUIDED_CHOICE",
      supportLevel: "FULL",
      retrievalEvidence: false,
      strategyEvidence: true,
    },
    {
      phase: LESSON_PHASES[3],
      title: "Bijna zelf",
      copy: copy.partial,
      generatorId: `${generatorPrefix}.partial`,
      responseMode: "NUMERIC_ENTRY",
      supportLevel: "PARTIAL",
      retrievalEvidence: false,
      strategyEvidence: true,
    },
    {
      phase: LESSON_PHASES[4],
      title: "Probeer zelf",
      copy: copy.independent,
      generatorId: `${generatorPrefix}.independent`,
      responseMode: "NUMERIC_ENTRY",
      supportLevel: "AVAILABLE",
      retrievalEvidence: false,
      strategyEvidence: true,
    },
  ];

  return { id, title, steps, exampleFactIds };
}

export const STRATEGIES: readonly StrategyDefinition[] = [
  {
    id: STRATEGY_IDS.ZERO_IDENTITY,
    label: "Nul verandert niets",
    summary: "Bij nul erbij of nul eraf blijft het getal gelijk; n min n is nul.",
    supportedOperations: ["ADD", "SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.ZERO],
    prerequisiteSkillIds: [],
    visualModels: ["PART_WHOLE"],
    lesson: lesson(
      "LESSON.ZERO_IDENTITY",
      "Wat doet nul?",
      "zero-identity",
      [factId("ADD", 8, 0, 8), factId("SUBTRACT", 8, 8, 0)],
      {
        explanation: ["Nul erbij verandert niets.", "Haal je alles weg, dan blijft nul over."],
        worked: ["8 + 0 blijft 8."],
        guided: ["Kijk wat er verandert als er niets bijkomt."],
        partial: ["Vul het antwoord in met de nulregel."],
        independent: ["Probeer een nieuwe nulsom zelf."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.COUNT_ON,
    label: "Een klein stukje verder",
    summary: "Bij plus 1 of plus 2 één of twee stappen verder tellen.",
    supportedOperations: ["ADD"],
    appliesToSkillIds: [SKILL_IDS.PLUS_MINUS_1, SKILL_IDS.PLUS_MINUS_2],
    prerequisiteSkillIds: [],
    visualModels: ["NUMBER_LINE"],
    lesson: lesson(
      "LESSON.COUNT_ON",
      "Tel een klein stukje verder",
      "count-on",
      [factId("ADD", 8, 1, 9), factId("ADD", 7, 2, 9)],
      {
        explanation: ["Bij plus 1 ga je één stap verder.", "Bij plus 2 ga je twee stappen verder."],
        worked: ["8 + 1 is de buur na 8: 9."],
        guided: ["Zet samen één of twee stappen."],
        partial: ["De eerste stap staat klaar; maak het af."],
        independent: ["Probeer een nieuwe buurgetalsom."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.COUNT_BACK,
    label: "Een klein stukje terug",
    summary: "Bij min 1 of min 2 één of twee stappen terug tellen.",
    supportedOperations: ["SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.PLUS_MINUS_1, SKILL_IDS.PLUS_MINUS_2],
    prerequisiteSkillIds: [],
    visualModels: ["NUMBER_LINE"],
    lesson: lesson(
      "LESSON.COUNT_BACK",
      "Tel een klein stukje terug",
      "count-back",
      [factId("SUBTRACT", 9, 1, 8), factId("SUBTRACT", 9, 2, 7)],
      {
        explanation: ["Bij min 1 ga je één stap terug.", "Bij min 2 ga je twee stappen terug."],
        worked: ["9 - 1 is de buur voor 9: 8."],
        guided: ["Zet samen één of twee stappen terug."],
        partial: ["De eerste stap staat klaar; maak het af."],
        independent: ["Probeer een nieuwe buurgetalsom."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.DOUBLES,
    label: "Dubbelen",
    summary: "Twee keer hetzelfde getal bij elkaar nemen.",
    supportedOperations: ["ADD", "SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.DOUBLES],
    prerequisiteSkillIds: [],
    visualModels: ["DOUBLES_VISUAL", "TEN_FRAME"],
    lesson: lesson(
      "LESSON.DOUBLES",
      "Twee keer hetzelfde",
      "doubles",
      [factId("ADD", 6, 6, 12), factId("SUBTRACT", 12, 6, 6)],
      {
        explanation: ["Bij een dubbel staan twee dezelfde getallen."],
        worked: ["6 + 6 is twee keer 6: samen 12."],
        guided: ["Maak twee gelijke groepjes."],
        partial: ["Gebruik het dubbel dat je al ziet."],
        independent: ["Probeer een nieuw dubbel zelf."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.MAKE_10,
    label: "Maak 10",
    summary: "Herkennen welke twee delen samen 10 vormen.",
    supportedOperations: ["ADD", "SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.MAKE_10],
    prerequisiteSkillIds: [SKILL_IDS.SPLIT_10],
    visualModels: ["TEN_FRAME", "NUMBER_BONDS"],
    lesson: lesson(
      "LESSON.MAKE_10",
      "Maak samen 10",
      "make-10",
      [factId("ADD", 8, 2, 10), factId("SUBTRACT", 10, 7, 3)],
      {
        explanation: ["Sommige getallen zijn vriendjes omdat ze samen 10 maken."],
        worked: ["8 heeft nog 2 nodig. Dus 8 + 2 = 10."],
        guided: ["Vul samen het ontbrekende vriendje in."],
        partial: ["Kijk naar het tienframe en maak het af."],
        independent: ["Vind zelf een nieuw vriendje van 10."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.NEAR_DOUBLE,
    label: "Bijna dubbel",
    summary: "Een bekend dubbel gebruiken en één erbij of eraf doen.",
    supportedOperations: ["ADD", "SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.NEAR_DOUBLES],
    prerequisiteSkillIds: [SKILL_IDS.DOUBLES],
    visualModels: ["DOUBLES_VISUAL", "PART_WHOLE"],
    lesson: lesson(
      "LESSON.NEAR_DOUBLE",
      "Gebruik een dubbel",
      "near-double",
      [factId("ADD", 6, 7, 13), factId("ADD", 8, 9, 17)],
      {
        explanation: ["Bij 6 + 7 helpt het dubbel 6 + 6.", "Daar komt nog 1 bij."],
        worked: ["6 + 6 = 12, dus 6 + 7 = 13."],
        guided: ["Kies eerst het dubbel dat helpt."],
        partial: ["Het dubbel staat klaar; tel nog één verder."],
        independent: ["Probeer een nieuwe bijna-dubbel."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.COMMUTE,
    label: "Omwisselen",
    summary: "Bij optellen mogen de getallen van plek wisselen.",
    supportedOperations: ["ADD"],
    appliesToSkillIds: [SKILL_IDS.COMMUTATIVE],
    prerequisiteSkillIds: [],
    visualModels: ["PART_WHOLE", "GROUPS"],
    lesson: lesson(
      "LESSON.COMMUTE",
      "Wissel de getallen om",
      "commute",
      [factId("ADD", 3, 7, 10), factId("ADD", 7, 3, 10)],
      {
        explanation: ["Bij optellen blijft het totaal gelijk als de getallen wisselen."],
        worked: ["3 + 7 en 7 + 3 zijn allebei 10."],
        guided: ["Draai samen een optelsom om."],
        partial: ["Eén kant staat klaar; vul de andere in."],
        independent: ["Gebruik omwisselen bij een nieuwe optelsom."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.INVERSE_FACT,
    label: "Plus en min horen bij elkaar",
    summary: "Een bekend optelfeit gebruiken om een aftrekfeit te vinden, of andersom.",
    supportedOperations: ["ADD", "SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.INVERSE],
    prerequisiteSkillIds: [],
    visualModels: ["NUMBER_BONDS", "PART_WHOLE"],
    lesson: lesson(
      "LESSON.INVERSE_FACT",
      "Dezelfde drie getallen",
      "inverse-fact",
      [factId("ADD", 7, 5, 12), factId("SUBTRACT", 12, 5, 7)],
      {
        explanation: ["7, 5 en 12 horen samen in één somfamilie."],
        worked: ["Omdat 7 + 5 = 12, weet je ook 12 - 5 = 7."],
        guided: ["Maak samen de bijbehorende minsom."],
        partial: ["De optelsom staat klaar; vul de inverse in."],
        independent: ["Gebruik een nieuwe somfamilie zelf."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.TEEN_ANALOGY,
    label: "Reken met het stukje na 10",
    summary: "Een feit binnen 10 toepassen op het losse deel van een tienergetal.",
    supportedOperations: ["ADD", "SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.TEEN_NO_BRIDGE],
    prerequisiteSkillIds: [SKILL_IDS.TEEN_STRUCTURE, SKILL_IDS.BASIC_TO_10],
    visualModels: ["TEN_FRAME", "PART_WHOLE"],
    lesson: lesson(
      "LESSON.TEEN_ANALOGY",
      "Laat de 10 staan",
      "teen-analogy",
      [factId("ADD", 13, 5, 18), factId("SUBTRACT", 18, 5, 13)],
      {
        explanation: ["Bij 13 + 5 blijft de 10 staan.", "Reken 3 + 5 en zet de 10 terug."],
        worked: ["3 + 5 = 8, dus 13 + 5 = 18."],
        guided: ["Splits samen het tienergetal in 10 en een rest."],
        partial: ["De 10 staat klaar; reken met de rest."],
        independent: ["Probeer een nieuwe som zonder tientalbrug."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.VIA_10_ADD,
    label: "Via 10 optellen",
    summary: "Eerst aanvullen tot 10 en daarna de rest erbij tellen.",
    supportedOperations: ["ADD"],
    appliesToSkillIds: [SKILL_IDS.CROSS_10_ADD],
    prerequisiteSkillIds: [
      SKILL_IDS.MAKE_10,
      SKILL_IDS.SPLIT_10,
      SKILL_IDS.TEEN_STRUCTURE,
    ],
    visualModels: ["TEN_FRAME", "NUMBER_BONDS", "PART_WHOLE"],
    lesson: lesson(
      "LESSON.VIA_10_ADD",
      "Maak eerst 10",
      "via-10-add",
      [
        factId("ADD", 8, 5, 13),
        factId("ADD", 7, 6, 13),
        factId("ADD", 9, 5, 14),
        factId("ADD", 8, 6, 14),
      ],
      {
        explanation: ["Bij een som over 10 maken we eerst 10.", "Daarna tellen we de rest erbij."],
        worked: ["8 heeft 2 nodig om 10 te worden.", "Van de 5 blijven er 3 over.", "10 + 3 = 13."],
        guided: ["Hoeveel heeft 7 nodig om 10 te worden?", "Hoeveel blijven er van de 6 over?"],
        partial: ["Maak eerst 10 en vul de ontbrekende delen in."],
        independent: ["Probeer een nieuwe som.", "Denk: eerst naar 10."],
      },
    ),
  },
  {
    id: STRATEGY_IDS.BACK_VIA_10,
    label: "Via 10 aftrekken",
    summary: "Eerst teruggaan naar 10 en daarna de rest eraf halen.",
    supportedOperations: ["SUBTRACT"],
    appliesToSkillIds: [SKILL_IDS.CROSS_10_SUB],
    prerequisiteSkillIds: [
      SKILL_IDS.MAKE_10,
      SKILL_IDS.SPLIT_10,
      SKILL_IDS.TEEN_STRUCTURE,
    ],
    visualModels: ["TEN_FRAME", "NUMBER_BONDS", "NUMBER_LINE"],
    lesson: lesson(
      "LESSON.BACK_VIA_10",
      "Ga eerst terug naar 10",
      "back-via-10",
      [
        factId("SUBTRACT", 15, 7, 8),
        factId("SUBTRACT", 14, 6, 8),
        factId("SUBTRACT", 13, 5, 8),
      ],
      {
        explanation: ["Bij aftrekken over 10 gaan we eerst terug naar 10.", "Daarna gaat de rest eraf."],
        worked: ["Van 15 haal je eerst 5 af.", "Van de 7 moeten er dan nog 2 af.", "10 - 2 = 8."],
        guided: ["Hoeveel moet er eerst af om op 10 te komen?", "Hoeveel moet er daarna nog af?"],
        partial: ["Ga eerst naar 10 en vul de ontbrekende delen in."],
        independent: ["Probeer een nieuwe som.", "Denk: eerst terug naar 10."],
      },
    ),
  },
];

export const STRATEGY_BY_ID: ReadonlyMap<string, StrategyDefinition> = new Map(
  STRATEGIES.map((strategy) => [strategy.id, strategy]),
);
